import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PaymentIcon from '@mui/icons-material/Payment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PosOrderPaymentDrawer from '../../modules/posRestaurant/components/PosOrderPaymentDrawer';
import { formatBookingCurrency, parseRestaurantOrdersSummary } from '../../utils/bookingPaymentSummary';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerHeaderSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
} from '../../utils/bookingDrawerLayout';

const REST_ACCENT = '#14b8a6';

function normalizePosOrder(order) {
  if (!order) return null;
  const total = Number(order.total_amount || 0);
  const paid = Number(order.paid_amount || 0);
  const remaining = Number(
    order.remaining_amount != null ? order.remaining_amount : Math.max(0, total - paid),
  );
  return {
    ...order,
    total_amount: total,
    partial_paid_amount: paid,
    due_amount: remaining,
    remaining_amount: remaining,
  };
}

/**
 * Opens restaurant (POS) payment collection for orders linked to a booking.
 * One order → PosOrderPaymentDrawer directly.
 * Multiple → order picker, then payment drawer.
 */
export default function BookingRestaurantPaymentDrawer({
  open,
  onClose,
  booking,
  isDark = false,
  onPaymentsChanged,
}) {
  const drawerTheme = getBookingDrawerTheme(isDark);
  const restaurant = useMemo(() => parseRestaurantOrdersSummary(booking), [booking]);
  const orders = restaurant.orders || [];
  const currency = booking?.currency || 'INR';
  const wasOpenRef = useRef(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      setSelectedOrder(null);
      setPickerOpen(false);
      return;
    }

    // Keep selection stable while open; only refresh amounts from booking.
    if (wasOpenRef.current) {
      setSelectedOrder((prev) => {
        if (!prev?.id) return prev;
        const updated = orders.find((o) => Number(o.id) === Number(prev.id));
        return updated ? normalizePosOrder(updated) : prev;
      });
      return;
    }
    wasOpenRef.current = true;

    if (orders.length === 0) {
      setSelectedOrder(null);
      setPickerOpen(false);
      return;
    }
    if (orders.length === 1) {
      setSelectedOrder(normalizePosOrder(orders[0]));
      setPickerOpen(false);
      return;
    }
    const dueOrders = orders.filter((o) => Number(o.remaining_amount || 0) > 0.009);
    if (dueOrders.length === 1) {
      setSelectedOrder(normalizePosOrder(dueOrders[0]));
      setPickerOpen(false);
      return;
    }
    setSelectedOrder(null);
    setPickerOpen(true);
  }, [open, orders]);

  const handleCloseAll = () => {
    setSelectedOrder(null);
    setPickerOpen(false);
    onClose?.();
  };

  const handlePaymentDrawerClose = () => {
    handleCloseAll();
  };

  const handlePaymentsChanged = async (payload) => {
    await onPaymentsChanged?.(payload);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(open && pickerOpen && !selectedOrder)}
        onClose={handleCloseAll}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: BOOKING_DRAWER_WIDTH_SX,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RestaurantIcon sx={{ color: REST_ACCENT, fontSize: 22 }} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: drawerTheme.text || (isDark ? '#ddd' : '#333'), lineHeight: 1.2 }}
                >
                  Restaurant Payments
                </Typography>
                <Typography variant="caption" sx={{ color: drawerTheme.subtleText || (isDark ? '#999' : '#666') }}>
                  Select an order to collect payment
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleCloseAll} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {orders.length === 0 ? (
              <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
                No restaurant orders linked to this booking.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {orders.map((order) => {
                  const due = Number(order.remaining_amount || 0);
                  const paid = Number(order.paid_amount || 0);
                  const total = Number(order.total_amount || 0);
                  return (
                    <Paper
                      key={order.id}
                      elevation={0}
                      onClick={() => {
                        setSelectedOrder(normalizePosOrder(order));
                        setPickerOpen(false);
                      }}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderRadius: '12px',
                        border: `1px solid ${
                          due > 0.009
                            ? alpha(REST_ACCENT, isDark ? 0.45 : 0.35)
                            : isDark
                              ? 'rgba(255,255,255,0.1)'
                              : 'rgba(0,0,0,0.08)'
                        }`,
                        backgroundImage: `linear-gradient(165deg, ${alpha(REST_ACCENT, isDark ? 0.12 : 0.08)} 0%, transparent 60%)`,
                        '&:hover': {
                          borderColor: REST_ACCENT,
                          backgroundColor: alpha(REST_ACCENT, isDark ? 0.1 : 0.06),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                            <Chip
                              size="small"
                              icon={<PaymentIcon sx={{ fontSize: '14px !important' }} />}
                              label={order.order_number || `Order ${order.id}`}
                              sx={{
                                height: 24,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.18 : 0.12),
                                color: BOOKING_DRAWER_ACCENT,
                                '& .MuiChip-icon': { color: BOOKING_DRAWER_ACCENT },
                              }}
                            />
                            {order.location_label ? (
                              <Typography variant="caption" sx={{ color: drawerTheme.subtleText }}>
                                {order.location_label}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, display: 'block' }}>
                            Total {formatBookingCurrency(total, currency)} · Paid{' '}
                            {formatBookingCurrency(paid, currency)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              fontWeight: 800,
                              color: due > 0.009 ? (isDark ? '#ffb74d' : '#f57c00') : (isDark ? '#81c784' : '#2e7d32'),
                            }}
                          >
                            Due {formatBookingCurrency(due, currency)}
                          </Typography>
                        </Box>
                        <ChevronRightIcon sx={{ color: REST_ACCENT, mt: 0.5 }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Box sx={{ p: 1.5, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e0e0e0'}` }}>
            <Button fullWidth variant="outlined" onClick={handleCloseAll} sx={{ borderRadius: '10px' }}>
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      <PosOrderPaymentDrawer
        open={Boolean(open && selectedOrder)}
        onClose={handlePaymentDrawerClose}
        order={selectedOrder}
        booking={booking}
        isDark={isDark}
        onPaymentsChanged={handlePaymentsChanged}
      />
    </>
  );
}
